import { Controller } from "@hotwired/stimulus"

/**
 * Help Drawer Controller
 *
 * Gère le panneau latéral d'aide contextuelle accessible depuis un FAB '?'.
 * Le contenu est rendu côté serveur (Markdown I18n) et injecté dans le DOM au chargement.
 *
 * RGAA 4.1 compliant avec focus trap.
 */
export default class extends Controller {
  static targets = ["drawer", "overlay", "fab"]

  static FOCUSABLE_SELECTORS = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    'input:not([type="hidden"]):not([disabled])',
    "select:not([disabled])",
    '[tabindex]:not([tabindex="-1"])'
  ].join(", ")

  connect() {
    this.previouslyFocusedElement = null
    this.isOpen = false
    this.bindGlobalShortcut()
  }

  disconnect() {
    this.unbindGlobalShortcut()
    this.releaseFocusTrap()
  }

  // === Actions publiques ===

  toggle(event) {
    event?.preventDefault()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    if (!this.hasDrawerTarget) return

    this.previouslyFocusedElement = document.activeElement
    this.isOpen = true
    this.drawerTarget.classList.remove("translate-x-full")
    this.drawerTarget.setAttribute("aria-hidden", "false")

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden")
    }
    if (this.hasFabTarget) {
      this.fabTarget.classList.add("hidden")
    }

    this.setupFocusTrap()
    this.focusFirstElement()
  }

  close() {
    if (!this.hasDrawerTarget) return

    this.isOpen = false
    this.drawerTarget.classList.add("translate-x-full")
    this.drawerTarget.setAttribute("aria-hidden", "true")

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden")
    }
    if (this.hasFabTarget) {
      this.fabTarget.classList.remove("hidden")
    }

    this.releaseFocusTrap()
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus()
      this.previouslyFocusedElement = null
    }
  }

  // === Focus Trap (RGAA 4.1) ===

  setupFocusTrap() {
    this.trapFocusHandler = this.trapFocus.bind(this)
    this.drawerTarget.addEventListener("keydown", this.trapFocusHandler)
  }

  releaseFocusTrap() {
    if (this.trapFocusHandler) {
      this.drawerTarget?.removeEventListener("keydown", this.trapFocusHandler)
      this.trapFocusHandler = null
    }
  }

  trapFocus(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }

    if (event.key !== "Tab") return

    const focusable = this.getFocusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  getFocusableElements() {
    return Array.from(
      this.drawerTarget.querySelectorAll(this.constructor.FOCUSABLE_SELECTORS)
    ).filter(el => el.offsetWidth > 0 && el.offsetHeight > 0)
  }

  focusFirstElement() {
    setTimeout(() => {
      const elements = this.getFocusableElements()
      if (elements.length > 0) elements[0].focus()
    }, 150)
  }

  // === Global Shortcut ===

  bindGlobalShortcut() {
    this.globalShortcutHandler = (event) => {
      if (event.key === "?" && !this.isTypingInField(event.target)) {
        event.preventDefault()
        this.toggle(event)
      }
    }
    document.addEventListener("keydown", this.globalShortcutHandler)
  }

  unbindGlobalShortcut() {
    if (this.globalShortcutHandler) {
      document.removeEventListener("keydown", this.globalShortcutHandler)
      this.globalShortcutHandler = null
    }
  }

  // Évite de déclencher le raccourci quand l'utilisateur tape dans un champ
  isTypingInField(element) {
    const tag = element.tagName
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" ||
           element.isContentEditable || !!element.closest("lexxy-editor, trix-editor")
  }
}
